<script module lang="ts">
	// Sharing with this shop's tills (T28, UC-58, UC-59, #775): on a Windows
	// till, the onboarding's first step (T27) and the Sheet that Till
	// settings' Sharing row opens (T26). One line, the mesh's state as a
	// standing Notice with Turn on sharing, and, once sharing is on, the
	// shop's other tills, each with its link test (LinkTest.svelte).
	//
	// Turn on sharing is T25's one button and one prompt; its run and wait
	// live with the mesh, through sharingOutcome and ALLOWED_WAIT
	// (decision 2026-10-18-till-turns-on-windows-sharing). A test that fails
	// says why (decision 2026-10-22-onboarding-tests-the-link). A test that
	// finds sharing off, or the till gone from the shop, reads the mesh again,
	// and its line stays above the list, where the till it named may no
	// longer show.
	import type { MessageKey } from '../i18n/messages';
	import { tillLine, type Mesh, type MeshTill, type MeshState } from '../till/mesh.svelte';

	/** The Notice's line and whether it carries Turn on sharing; a state the screen does not know reads as off. */
	export function sharingNotice(
		state: MeshState | null,
		allowed: boolean,
	): { key: MessageKey; action: boolean } {
		if (state === 'on') return { key: 'sharingOn', action: false };
		if (state === 'needs_firewall')
			return { key: allowed ? 'printersShareAllowed' : 'sharingNeedsYes', action: !allowed };
		return { key: 'sharingOff', action: false };
	}

	/** T25's flag Band for Windows' no, or for a command that failed. */
	export function sharingRefusalKey(why: 'declined' | 'failed'): MessageKey {
		return why === 'declined' ? 'printersShareDeclined' : 'printersShareFailed';
	}

	/** A refusal shows only while sharing still needs Windows' yes, as on T25. */
	export function sharingRefusalShows(
		why: 'declined' | 'failed' | null,
		state: MeshState | null,
	): boolean {
		return why !== null && state === 'needs_firewall';
	}

	/** Whether a link test's lost line still applies to the mesh state. */
	export function lostLineShows(
		lost: 'linkMeshOff' | 'linkGone' | null,
		state: MeshState | null,
	): boolean {
		return lost === 'linkGone' || (lost === 'linkMeshOff' && state !== 'on');
	}

	/**
	 * What the step shows for its mesh read: nothing for the first read's
	 * first 300 ms, then the compass; a failure, over the last mesh read if
	 * any; or the mesh.
	 */
	export function sharingReadState(mesh: {
		loading: boolean;
		slow: boolean;
		failed: boolean;
		settled: boolean;
		mesh: Mesh | null;
	}): 'waiting' | 'loading' | 'failed' | 'ready' {
		if (!mesh.settled && !mesh.mesh) return mesh.slow ? 'loading' : 'waiting';
		return mesh.failed ? 'failed' : 'ready';
	}

	/** A till's line under its name: when it was heard, not yet, or an older app. */
	export function tillLineKey(till: MeshTill): MessageKey {
		const keys = {
			heard: 'sharingHeard',
			not_heard: 'sharingNotHeard',
			legacy: 'sharingLegacy',
		} as const;
		return keys[tillLine(till)];
	}
</script>

<script lang="ts">
	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Loading from '../components/Loading.svelte';
	import Notice from '../components/Notice.svelte';
	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { formatWhen } from '../format/time';
	import { fill, message, type Language } from '../i18n/messages';
	import { tillTestable, type TillMesh } from '../till/mesh.svelte';
	import { turnOnSharing } from '../till/shell';
	import LinkTest from './LinkTest.svelte';
	import { ALLOWED_WAIT, sharingOutcome } from './Printers.svelte';

	interface Props {
		language: Language;
		mesh: TillMesh;
		/** The step's title; off in T26's Sheet, whose title says it. */
		heading?: boolean;
		/** A pass is on its way in the onboarding: presses do nothing. */
		disabled?: boolean;
	}
	let { language, mesh, heading = true, disabled = false }: Props = $props();
	let sharingLost = $state<{ key: 'linkMeshOff' | 'linkGone'; name: string } | null>(null);
	const readState = $derived(sharingReadState(mesh));
	const notice = $derived(sharingNotice(mesh.state, mesh.allowed));

	function turnOn() {
		if (mesh.turning || disabled) return;
		sharingLost = null;
		void mesh.turnOn(async () => sharingOutcome(await turnOnSharing()), ALLOWED_WAIT);
	}

	// Read the mesh at once and every few seconds while the step shows; the
	// watch hangs on the mesh alone, so a read it starts never restarts it.
	$effect(() => {
		const watched = mesh;
		return untrack(() => watched.watch());
	});

	function lost(key: 'linkMeshOff' | 'linkGone', name: string) {
		sharingLost = { key, name };
		void mesh.read();
	}
</script>

<div class="step">
	{#if heading}<h3>{message(language, 'sharingTitle')}</h3>{/if}
	<p>{message(language, 'sharingIntro')}</p>
	{#if readState === 'loading'}
		<Loading
			label={message(language, 'tillSettingsLoading')}
			caption={message(language, 'tillSettingsLoading')}
		/>
	{/if}
	{#if readState === 'failed'}
		<Band
			tone="flag"
			alert
			testId="sharing-read-error"
			action={mesh.retryable ? message(language, 'doneRetry') : undefined}
			actionTestId="sharing-retry"
			actionBusy={mesh.loading}
			onAction={mesh.retryable ? () => void mesh.read() : undefined}
		>
			{message(language, 'sharingReadFailed')}
		</Band>
	{/if}
	{#if mesh.mesh}
		<Notice
			life="standing"
			tone="neutral"
			testId="sharing-state"
			action={notice.action ? message(language, 'printersShareTurnOn') : undefined}
			actionTestId="sharing-turn-on"
			actionBusy={mesh.turning}
			onAction={() => void turnOn()}
		>
			{message(language, notice.key)}
		</Notice>
		{#if mesh.refusal && sharingRefusalShows(mesh.refusal, mesh.state)}<Band
				tone="flag"
				alert
				testId="sharing-refusal">{message(language, sharingRefusalKey(mesh.refusal))}</Band
			>{/if}
	{/if}
	{#if sharingLost && lostLineShows(sharingLost.key, mesh.state)}
		<Band tone="flag" alert testId="sharing-lost"
			>{fill(message(language, sharingLost.key), sharingLost.name)}</Band
		>
	{/if}
	{#if mesh.state === 'on' && mesh.mesh}
		{#if mesh.mesh.tills.length === 0}
			<p data-testid="sharing-no-tills">{message(language, 'sharingNoTills')}</p>
		{:else}
			<TablePanel
				columns={[
					{ label: message(language, 'sharingTill'), w: 'minmax(0, 1fr)' },
					{ label: message(language, 'sharingLinkColumn'), w: 'minmax(0, 1.5fr)' },
				]}
			>
				{#each mesh.mesh.tills as till (till.tillId)}
					<Row testId="sharing-till-{till.tillId}">
						<div class="till">
							<strong>{till.name}</strong>
							<span data-testid="sharing-heard-{till.tillId}"
								>{fill(
									message(language, tillLineKey(till)),
									formatWhen(till.heardAt, language, {
										today: message(language, 'sharingHeardToday'),
										yesterday: message(language, 'sharingHeardYesterday'),
									}),
								)}</span
							>
						</div>
						<div>
							{#if tillTestable(till)}
								<LinkTest
									{language}
									tillId={till.tillId}
									name={till.name}
									{disabled}
									onStart={() => (sharingLost = null)}
									onMeshOff={(name) => lost('linkMeshOff', name)}
									onGone={(name) => lost('linkGone', name)}
								/>
							{/if}
						</div>
					</Row>
				{/each}
			</TablePanel>
		{/if}
	{/if}
</div>

<style>
	.step {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}
	h3 {
		margin: 0;
		font-size: var(--text-h4);
	}
	p {
		margin: 0;
	}
	.till {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		min-inline-size: 0;
	}
	.till span {
		color: var(--text-muted);
	}
</style>
